import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Buildings, Fire, Wheelchair, Moon, SpeakerHigh, ArrowRight, CheckCircle, Phone, Envelope, MapPin, List, X } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Navigation Header
const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="font-bold text-2xl tracking-tighter font-['Outfit']" data-testid="logo">
            CARRÉ <span className="text-[#E53935]">CHR</span>
          </Link>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#expertises" className="text-sm font-medium text-[#52525B] hover:text-[#0A0A0A] transition-colors">Expertises</a>
            <a href="#tarifs" className="text-sm font-medium text-[#52525B] hover:text-[#0A0A0A] transition-colors">Tarifs</a>
            <a href="#contact" className="text-sm font-medium text-[#52525B] hover:text-[#0A0A0A] transition-colors">Contact</a>
            
            {user ? (
              <div className="flex items-center gap-4">
                <Button 
                  onClick={() => navigate(user.role === 'architecte' ? '/architect' : user.role === 'admin' ? '/admin' : '/client')}
                  className="bg-[#0A0A0A] hover:bg-[#27272A] rounded-none"
                  data-testid="dashboard-btn"
                >
                  Tableau de bord
                </Button>
                <button 
                  onClick={logout}
                  className="text-sm text-[#52525B] hover:text-[#0A0A0A]"
                  data-testid="logout-btn"
                >
                  Déconnexion
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" className="text-sm font-medium text-[#52525B] hover:text-[#0A0A0A]" data-testid="login-link">Connexion</Link>
                <Button 
                  onClick={() => navigate('/register')}
                  className="bg-[#E53935] hover:bg-[#D32F2F] rounded-none"
                  data-testid="register-btn"
                >
                  Commencer
                </Button>
              </div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <nav className="md:hidden py-4 border-t border-[#E5E7EB]">
            <div className="flex flex-col gap-4">
              <a href="#expertises" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>Expertises</a>
              <a href="#tarifs" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>Tarifs</a>
              <a href="#contact" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>Contact</a>
              {user ? (
                <>
                  <Button 
                    onClick={() => { navigate(user.role === 'architecte' ? '/architect' : '/client'); setMobileOpen(false); }}
                    className="bg-[#0A0A0A] rounded-none w-full"
                  >
                    Tableau de bord
                  </Button>
                  <button onClick={() => { logout(); setMobileOpen(false); }} className="text-sm text-left">Déconnexion</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-medium" onClick={() => setMobileOpen(false)}>Connexion</Link>
                  <Button onClick={() => { navigate('/register'); setMobileOpen(false); }} className="bg-[#E53935] rounded-none w-full">Commencer</Button>
                </>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const Hero = () => {
  const navigate = useNavigate();
  
  return (
    <section className="relative min-h-screen flex items-center pt-16">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/e253916e-e5bb-46ac-bf5f-25341dda13f7/images/02b76b4345847fc9bad6ffb43240a45fc6339d870629274fece55c2040f7511e.png')` }}
      >
        <div className="absolute inset-0 bg-black/40" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/80 mb-4 animate-fade-in-up">
            Opérateur de conformité intégré
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none text-white mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            La conformité de votre établissement,<br />
            <span className="font-medium">simplifiée.</span>
          </h1>
          <p className="text-lg text-white/80 mb-8 max-w-xl animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Un seul interlocuteur pour vos 5 pôles de conformité : terrasses, sécurité incendie, accessibilité PMR, licences de nuit et sonorisation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <Button 
              onClick={() => navigate('/register')}
              className="bg-[#E53935] hover:bg-[#D32F2F] rounded-none px-8 py-6 text-lg shadow-[4px_4px_0px_0px_rgba(255,255,255,0.2)]"
              data-testid="hero-cta-btn"
            >
              Demander un devis <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              variant="outline"
              onClick={() => document.getElementById('expertises').scrollIntoView({ behavior: 'smooth' })}
              className="bg-transparent border-white text-white hover:bg-white/10 rounded-none px-8 py-6 text-lg"
              data-testid="hero-discover-btn"
            >
              Découvrir nos services
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Expertise Cards
const expertises = [
  {
    id: 'terrasse',
    icon: Buildings,
    title: 'Terrasses',
    description: 'Autorisations de terrasses permanentes et estivales conformes au RET parisien.',
    image: 'https://images.unsplash.com/photo-1642486574545-509db153db60?w=600&q=80',
    services: ['Pré-diagnostic de faisabilité', 'Plans à échelle conformes', 'Dépôt via Mon Paris', 'Suivi jusqu\'à autorisation']
  },
  {
    id: 'securite',
    icon: Fire,
    title: 'Sécurité Incendie',
    description: 'Plans d\'intervention, registres de sécurité ERP et formation du personnel.',
    image: 'https://images.unsplash.com/photo-1766498737171-dd71945565ad?w=600&q=80',
    services: ['Plans NF X 08-070', 'Registres de sécurité', 'Formation équipe', 'Préparation commissions']
  },
  {
    id: 'accessibilite',
    icon: Wheelchair,
    title: 'Accessibilité PMR',
    description: 'Diagnostics complets et dossiers de dérogation pour la mise en conformité.',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&q=80',
    services: ['Diagnostic complet', 'Dossiers dérogation', 'Registre accessibilité', 'Financement identifié']
  },
  {
    id: 'licence',
    icon: Moon,
    title: 'Licences de Nuit',
    description: 'Obtention et transfert de licences III/IV, autorisations d\'ouverture nocturne.',
    image: 'https://static.prod-images.emergentagent.com/jobs/e253916e-e5bb-46ac-bf5f-25341dda13f7/images/c4e9a7c073fb6d4e571f8f9ee133eebe26810e60cdf40b130b3a5f7b195552da.png',
    services: ['Mutation/transfert licences', 'Déclaration Préfecture', 'Autorisation nuit', 'Formation obligatoire']
  },
  {
    id: 'sonorisation',
    icon: SpeakerHigh,
    title: 'Sonorisation',
    description: 'Études d\'impact sonore et installation de limiteurs de pression acoustique.',
    image: 'https://images.unsplash.com/photo-1598368195835-91e67f80c9d7?w=600&q=80',
    services: ['Études EINS', 'Installation LPA', 'Certificats conformité', 'Inspections biennales']
  }
];

const ExpertiseSection = () => {
  return (
    <section id="expertises" className="py-24 md:py-32 bg-[#FDFDFD]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E53935] mb-4">Nos expertises</p>
          <h2 className="text-4xl sm:text-5xl font-medium tracking-tight text-[#0A0A0A]">
            5 pôles d'expertise,<br />un seul interlocuteur
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* All cards same format */}
          {expertises.map((exp) => {
            const ExpIcon = exp.icon;
            return (
              <div key={exp.id} className="card-marketing group" data-testid={`expertise-${exp.id}`}>
                <ExpIcon size={40} className="text-[#E53935] mb-4" weight="duotone" />
                <h3 className="text-xl font-medium text-[#0A0A0A] mb-2">{exp.title}</h3>
                <p className="text-sm text-[#52525B] mb-4">{exp.description}</p>
                <ul className="space-y-1">
                  {exp.services.slice(0, 2).map((service, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-[#52525B]">
                      <CheckCircle size={14} className="text-[#16A34A]" weight="fill" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const pricingData = [
  { name: 'Terrasse estivale', price: '1 290€ - 1 990€', category: 'terrasse' },
  { name: 'Terrasse permanente ouverte', price: '2 990€ - 3 990€', category: 'terrasse' },
  { name: 'Terrasse permanente fermée', price: '3 990€ - 5 990€', category: 'terrasse' },
  { name: 'Plan intervention NF X 08-070', price: '390€ - 690€', category: 'securite', highlight: true },
  { name: 'Pack incendie complet', price: '1 690€ - 2 990€', category: 'securite' },
  { name: 'Diagnostic accessibilité + registre', price: '1 290€ - 1 990€', category: 'accessibilite' },
  { name: 'Dossier dérogation accessibilité', price: '1 690€ - 2 990€', category: 'accessibilite' },
  { name: 'Licence III/IV mutation/transfert', price: '1 690€ - 3 390€', category: 'licence' },
  { name: 'Autorisation ouverture de nuit', price: '1 290€ - 2 490€', category: 'licence' },
  { name: 'Étude impact sonore + LPA', price: '1 990€ - 3 790€', category: 'sonorisation' },
  { name: 'Pack Nuit complet', price: '3 990€ - 6 790€', category: 'licence' },
];

const PricingSection = () => {
  return (
    <section id="tarifs" className="py-24 md:py-32 bg-[#F4F4F5]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E53935] mb-4">Tarification</p>
          <h2 className="text-4xl sm:text-5xl font-medium tracking-tight text-[#0A0A0A] mb-4">
            Des forfaits clairs et transparents
          </h2>
          <p className="text-[#52525B] max-w-2xl mx-auto">
            Un seul interlocuteur, un forfait, un délai garanti.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#E5E7EB]">
          {pricingData.map((item, index) => (
            <div 
              key={index} 
              className={`p-6 border-b border-r border-[#E5E7EB] bg-white ${item.highlight ? 'bg-[#FEF2F2]' : ''}`}
              data-testid={`pricing-${index}`}
            >
              <p className="text-sm text-[#52525B] mb-1">{item.name}</p>
              <p className="text-xl font-semibold text-[#0A0A0A]">{item.price}</p>
              {item.highlight && (
                <span className="inline-block mt-2 text-xs font-bold uppercase tracking-wider text-[#E53935]">Produit d'appel</span>
              )}
            </div>
          ))}
          
          {/* Pack Ouverture */}
          <div className="md:col-span-2 lg:col-span-3 p-8 bg-[#0A0A0A] text-white" data-testid="pricing-pack-ouverture">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E53935] mb-2">Pack Complet</p>
                <h3 className="text-2xl font-medium mb-1">PACK OUVERTURE (5 pôles)</h3>
                <p className="text-white/60">Solution clé en main pour votre conformité totale</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">9 990€ - 14 990€</p>
                <p className="text-sm text-white/60">Forfait tout inclus</p>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription */}
        <div className="mt-8 p-8 border border-[#E5E7EB] bg-white" data-testid="pricing-subscription">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#16A34A] mb-2">Abonnement mensuel</p>
              <h3 className="text-xl font-medium text-[#0A0A0A] mb-1">Abonnement Sérénité</h3>
              <p className="text-[#52525B]">Suivi de conformité continue, alertes réglementaires, rappels automatisés</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#0A0A0A]">129€ - 169€<span className="text-base font-normal">/mois</span></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', pole: '', message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Demande envoyée ! Nous vous recontacterons rapidement.');
      setFormData({ name: '', email: '', phone: '', company: '', pole: '', message: '' });
    } catch (err) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#FDFDFD]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E53935] mb-4">Contact</p>
            <h2 className="text-4xl sm:text-5xl font-medium tracking-tight text-[#0A0A0A] mb-6">
              Parlons de votre projet
            </h2>
            <p className="text-[#52525B] mb-8">
              Remplissez le formulaire ou contactez-nous directement. Notre équipe vous répondra sous 24h.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F4F4F5] flex items-center justify-center">
                  <Phone size={24} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#52525B]">Téléphone</p>
                  <p className="font-medium">06 50 76 42 10</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F4F4F5] flex items-center justify-center">
                  <Envelope size={24} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#52525B]">Email</p>
                  <p className="font-medium">contact@carrechr.fr</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#F4F4F5] flex items-center justify-center">
                  <MapPin size={24} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#52525B]">Adresse</p>
                  <p className="font-medium">Paris, France</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Nom *</label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="rounded-none border-[#E5E7EB] focus:border-[#0A0A0A]"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Email *</label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="rounded-none border-[#E5E7EB] focus:border-[#0A0A0A]"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Téléphone</label>
                <Input 
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="rounded-none border-[#E5E7EB] focus:border-[#0A0A0A]"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Entreprise</label>
                <Input 
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="rounded-none border-[#E5E7EB] focus:border-[#0A0A0A]"
                  data-testid="contact-company-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Pôle d'expertise *</label>
              <Select value={formData.pole} onValueChange={(val) => setFormData({...formData, pole: val})}>
                <SelectTrigger className="rounded-none border-[#E5E7EB]" data-testid="contact-pole-select">
                  <SelectValue placeholder="Sélectionnez un pôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="terrasse">Terrasses</SelectItem>
                  <SelectItem value="securite">Sécurité Incendie</SelectItem>
                  <SelectItem value="accessibilite">Accessibilité PMR</SelectItem>
                  <SelectItem value="licence">Licences de Nuit</SelectItem>
                  <SelectItem value="sonorisation">Sonorisation</SelectItem>
                  <SelectItem value="pack">Pack Ouverture (5 pôles)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Message *</label>
              <Textarea 
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                required
                rows={4}
                className="rounded-none border-[#E5E7EB] focus:border-[#0A0A0A]"
                data-testid="contact-message-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#0A0A0A] hover:bg-[#27272A] rounded-none py-6"
              data-testid="contact-submit-btn"
            >
              {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A] text-white py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-2">
            <p className="font-bold text-2xl tracking-tighter font-['Outfit'] mb-4">
              CARRÉ <span className="text-[#E53935]">CHR</span>
            </p>
            <p className="text-white/60 max-w-sm">
              Opérateur de conformité intégré pour les établissements CHR parisiens. Un seul interlocuteur pour toutes vos démarches réglementaires.
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-4">Expertises</p>
            <ul className="space-y-2 text-sm text-white/60">
              <li>Terrasses</li>
              <li>Sécurité Incendie</li>
              <li>Accessibilité PMR</li>
              <li>Licences de Nuit</li>
              <li>Sonorisation</li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-4">Liens</p>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link to="/login" className="hover:text-white transition-colors">Espace Client</Link></li>
              <li><Link to="/register?role=architecte" className="hover:text-white transition-colors">Espace Architecte</Link></li>
              <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/40">© 2024 CARRÉ CHR. Tous droits réservés.</p>
          <p className="text-sm text-white/40">Partenaire officiel UMIH Paris</p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Header />
      <Hero />
      <ExpertiseSection />
      <PricingSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default LandingPage;
